import { Component, OnInit, Inject, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { SupplierDocumentPropertyValues, DocumentType, OPERATION, SupplierPropertyValue, Supplier, SupplierDocument } from 'app/profile/models/supplier.model';
import { SupplierDocumentService } from 'app/profile/services/supplier-document.service';
import { TslCheckboxChange, TslFileDrop, TslDialog } from '@tesla/ng-tds';
import { FileUploadService } from '../../../../common/services/fileUpload.service';
import { SupplierState } from 'app/profile/store/state/supplierstate.state';
import { Store } from '@ngrx/store';
import { PostEvent } from 'app/profile/store/app.actions';
import { SelectEventData, getGeneralSettings } from 'app/profile/store/app.selector';
import { getNestedNode } from 'app/shared/utils/utils';
import { GeneralSettings } from 'app/profile/models/generic.model';
import { I18nService } from 'app/shared/services/i18n.service';

@Component({
    selector: 'app-document-dialog',
    templateUrl: './document-dialog.component.html',
    styleUrls: ['./document-dialog.component.scss']
})
export class DocumentDialogComponent implements OnInit {
    @ViewChild('fileDrop', { static: true }) fileDrop: TslFileDrop;
    settings: GeneralSettings;
    editForm: FormGroup;
    types: DocumentType[] = [];
    file: File;
    supplierProperty = new SupplierPropertyValue();

    get fileSizeLimitMB() { return this.settings.fileSizeLimitMB * 1000000; }
    get noEffectiveDate() {
        return this.editForm.get('noEffectiveDate');
    }
    get noExpirationDate() {
        return this.editForm.get('noExpirationDate');
    }
    get noSignedDate() {
        return this.editForm.get('noSignedDate');
    }
    get hasFile() {
        return this.file || this.attachment.value;
    }
    get attachment() {
        return this.editForm.get('attachment');
    }

    constructor(@Inject(MAT_DIALOG_DATA) public data: { supplierCode: string, address: string, model: SupplierDocumentPropertyValues},
        private fb: FormBuilder,
        private store: Store<SupplierState>,
        private translateService: I18nService,
        private documentService: SupplierDocumentService,
        private fileUploadService: FileUploadService,
        private dialog: TslDialog,
        private dialogRef: MatDialogRef<DocumentDialogComponent>) {
                }

    ngOnInit() {
        this.loadResources();

        this.editForm = this.fb.group({
            supplier: [{value: this.data.supplierCode, disabled: true}],
            address: [{value: this.data.address, disabled: true}],
            category: [],
            type: [this.data.model.documentType.newValue, Validators.required],
            name: [this.data.model.fileName.newValue, Validators.required],
            effectiveDate: [this.data.model.effectiveDate.newValue, Validators.required],
            noEffectiveDate: [this.data.model.noEffectiveDate.newValue],
            expirationDate: [this.data.model.expiryDate.newValue, Validators.required],
            noExpirationDate: [this.data.model.noEffectiveDate.newValue],
            signatureDate: [this.data.model.signedDate.newValue, Validators.required],
            noSignedDate: [this.data.model.noSignedDate.newValue],
            comment: [],
            attachment: [this.data.model.attachment.newValue, Validators.required],
            });
        }

    onNoCheckedChange(change: TslCheckboxChange): void {
        switch (change.source.name) {
            case 'noEffectiveDate': {
                if (change.source.checked) {
                    this.editForm.get('effectiveDate').clearValidators();
                    this.editForm.get('effectiveDate').disable();
            } else {
                    this.editForm.get('effectiveDate').setValidators([Validators.required]);
                    this.editForm.get('effectiveDate').enable();
                }
                break;
            }
            case 'noExpirationDate': {
                if (change.source.checked) {
                    this.editForm.get('expirationDate').clearValidators();
                    this.editForm.get('expirationDate').disable();
            } else {
                    this.editForm.get('expirationDate').setValidators([Validators.required]);
                    this.editForm.get('expirationDate').enable();
            }
                break;
            }
            case 'noSignedDate': {
                if (change.source.checked) {
                    this.editForm.get('signatureDate').clearValidators();
                    this.editForm.get('signatureDate').disable();
            } else {
                    this.editForm.get('signatureDate').setValidators([Validators.required]);
                    this.editForm.get('signatureDate').enable();
                }
                break;
            }
        }
            }

    onFileAdd(files: any): void {
        const file = files[0];
        if (file) {
            if (file.size > this.fileSizeLimitMB) {
                this.dialog.alert(this.translateService.get('Alert'), this.translateService.get('File size can not be more than 10MB!'));
            } else {
                this.file = file;
                this.attachment.setValue(file);
            }
        }
            }

    onRemoveAttachment(): void {
        this.file = undefined;
        this.attachment.setValue('');
    }

    onSave(): void {
        if (this.editForm.invalid) {
            return;
    }

        const data = this.data.model;
        data.fileName.newValue = this.editForm.get('name').value;
        data.documentType.newValue = this.editForm.get('type').value;
        data.noEffectiveDate.newValue = this.editForm.get('noEffectiveDate').value;
        data.noExpirationDate.newValue = this.editForm.get('noExpirationDate').value;
        data.noSignedDate.newValue = this.editForm.get('noSignedDate').value;
        this.saveDocument(data);
    }

    onClose(): void {
        this.dialogRef.close();
    }

    private saveDocument(model: SupplierDocumentPropertyValues): void {
        if (this.file) {
            // create
            this.fileUploadService.uploadFileToServer([this.file], this.data.supplierCode)
                .subscribe(doc => {
                    if (doc) {
                        model.operation.newValue = OPERATION.CREATE;
                        model.documentID.newValue = doc.documentID;
                        model.documentVersion.newValue = doc.documentVersion;

                        this.supplierProperty.documents = [model];
                        this.postEvent(this.supplierProperty);
                }
                });
        } else {
            // update
            this.supplierProperty.documents = [model];
            this.postEvent(this.supplierProperty);
                }
                }

    private postEvent(eventProperties: SupplierPropertyValue) {
        this.store.dispatch(new PostEvent({
            Name: 'Document Change Requested',
            Properties: this.pickNewValues(eventProperties),
            ReferenceIdentifier: this.data.supplierCode
        }));
        this.store.select(SelectEventData).subscribe((evt: any) => {
            if (evt) {
                console.log(evt);
                }
        });
                }

    private pickNewValues(supplierData: SupplierPropertyValue): Supplier {
        if (!supplierData) {
            return null;
                }

        const supplier = new Supplier();
        if (supplierData.documents) {
            supplier.Documents = [];
            supplierData.documents.forEach((document, index) => {
                supplier.Documents[index] = new SupplierDocument();
                Object.keys(document).forEach(name => {
                    supplier.Documents[index][name] = getNestedNode([name, 'newValue'], document);
                });
                supplier.Documents[index].Operation = document.operation.newValue ? document.operation.newValue : null;
            });
                }
        return supplier;
                }

    private loadResources(): void {
        this.store.select(getGeneralSettings).subscribe(res => this.settings = res);
        this.documentService.getDocumentType().subscribe(res => this.types = res);
    }
}